import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Card, PageHeader } from "@/components/ui-helpers";
import { DuplicateGuard } from "@/components/DuplicateGuard";
import { SlotGrid } from "@/components/SlotGrid";
import { toast } from "sonner";
import { CalendarCheck, Zap, CheckCircle2, ArrowLeft } from "lucide-react";

const addMinutes = (hhmm, mins) => {
  const [h, m] = hhmm.split(":").map(Number);
  const t = h * 60 + m + mins;
  return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function BookAppointment() {
  const [staff, setStaff] = useState([]);
  const [settings, setSettings] = useState(null);
  const [avail, setAvail] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [f, setF] = useState({
    name: "", email: "", phone: "", type: "", service_interested: "", date: todayStr(),
    staff_id: "", start_time: "", end_time: "", meeting_type: "Video", notes: "", is_emergency: false,
  });

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/settings").then((r) => { setSettings(r.data); setF((p) => ({ ...p, type: (r.data.appointment_types || [])[0] || "Initial Consultation" })); }).catch(() => {});
  }, []);

  const duration = settings?.appointment_duration || 30;
  const types = settings?.appointment_types || [];
  const services = useMemo(() => {
    if (!settings) return [];
    const master = (settings.fee_schedule || []).filter((m) => m.professional_fee != null).map((m) => m.service);
    return [...master, ...((settings.services || []).filter((x) => !master.includes(x)))];
  }, [settings]);
  const isEmergency = f.is_emergency || f.type === "Emergency Appointment";

  useEffect(() => {
    if (!f.date) return;
    setLoadingAvail(true);
    const qs = new URLSearchParams({ date: f.date });
    if (f.staff_id) qs.set("staff_id", f.staff_id);
    let stale = false;
    api.get(`/appointments/availability?${qs.toString()}`).then((r) => { if (!stale) setAvail(r.data); }).catch(() => { if (!stale) setAvail(null); }).finally(() => { if (!stale) setLoadingAvail(false); });
    return () => { stale = true; };
  }, [f.date, f.staff_id]);

  const pickSlot = (s) => setF((p) => ({ ...p, start_time: s, end_time: addMinutes(s, duration) }));
  const setStart = (v) => setF((p) => ({ ...p, start_time: v, end_time: v ? addMinutes(v, duration) : "" }));

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Enter the person's name"); return; }
    if (isEmergency && !f.notes.trim()) { toast.error("Add a short reason for the emergency appointment"); return; }
    if (!f.start_time) { toast.error("Pick a time slot"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/appointments/book", { ...f, is_emergency: isEmergency });
      setResult(data);
      toast.success("Appointment booked");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const inputCls = "mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30";

  if (result) {
    const linkedLabel = result.created_lead ? "a new lead was created and linked"
      : result.linked === "client" ? "linked to the existing client" : "linked to the existing lead";
    const profile = result.client_id ? `/admin/clients/${result.client_id}` : null;
    const ap = result.appointment;
    return (
      <div className="max-w-xl mx-auto">
        <Card className="p-8 text-center" data-testid="booking-confirmation">
          <div className="h-14 w-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-emerald-600" /></div>
          <h2 className="font-head font-extrabold text-2xl text-slate-900">Appointment booked</h2>
          <p className="text-slate-500 mt-2 text-sm"><b>{result.contact_name}</b> — {linkedLabel}.</p>
          <div className="mt-5 text-left bg-slate-50 rounded-xl p-4 text-sm space-y-1">
            <div><b>Type:</b> {ap.type}</div>
            <div><b>Date:</b> {ap.date} at {ap.start_time}{ap.end_time ? `–${ap.end_time}` : ""}</div>
            <div><b>Meeting:</b> {ap.meeting_type}</div>
            <div><b>Status:</b> {ap.status}</div>
          </div>
          <div className="flex flex-wrap gap-2 justify-center mt-6">
            <button data-testid="book-another" onClick={() => { setResult(null); setF((p) => ({ ...p, name: "", email: "", phone: "", start_time: "", end_time: "", notes: "" })); }} className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm">Book another</button>
            <Link to="/admin/appointments" data-testid="go-to-appointments" className="bg-secondary text-white px-5 py-2.5 rounded-xl font-semibold text-sm">View appointments</Link>
            {profile && <Link to={profile} className="border border-slate-200 px-5 py-2.5 rounded-xl font-semibold text-sm text-slate-700">Open client</Link>}
          </div>
        </Card>
      </div>
    );
  }

  const slots = avail?.slots || [];
  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/admin/appointments" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-4"><ArrowLeft className="h-4 w-4" /> Back to appointments</Link>
      <PageHeader title="Book an Appointment" subtitle="Books straight into the CRM — links to the existing client or lead, or creates a new lead automatically." />
      <Card className="p-6 space-y-5" data-testid="book-form">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Who is this for?</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-slate-500">Full name *</label><input data-testid="book-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} /></div>
            <div><label className="text-xs font-medium text-slate-500">Email</label><input data-testid="book-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} /></div>
            <div><label className="text-xs font-medium text-slate-500">Phone</label><input data-testid="book-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} /></div>
          </div>
          <div className="mt-2"><DuplicateGuard email={f.email} phone={f.phone} testId="book-duplicate-warning" /></div>
          <p className="text-[11px] text-slate-400 mt-1">If this email/phone matches an existing client or lead, the appointment links to them — no duplicate is created.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Appointment type</label>
            <select data-testid="book-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inputCls}>{types.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label className="text-xs font-medium text-slate-500">Service interested (optional)</label>
            <select data-testid="book-service" value={f.service_interested} onChange={(e) => setF({ ...f, service_interested: e.target.value })} className={inputCls}><option value="">—</option>{services.map((s) => <option key={s}>{s}</option>)}</select></div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Consultant / Staff</label>
            <select data-testid="book-staff" value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value, start_time: "", end_time: "" })} className={inputCls}><option value="">Unassigned</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <div><label className="text-xs font-medium text-slate-500">Meeting type</label>
            <select data-testid="book-meeting" value={f.meeting_type} onChange={(e) => setF({ ...f, meeting_type: e.target.value })} className={inputCls}>{["Video", "Phone", "In Person"].map((m) => <option key={m}>{m}</option>)}</select></div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500">Date</label>
          <input data-testid="book-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value, start_time: "", end_time: "" })} className={inputCls} />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-500">Available time slots {f.staff_id ? "" : "(select a staff member to see their free slots)"}</label>
            {f.start_time && <span className="text-xs font-semibold text-primary" data-testid="book-selected-slot">Selected: {f.start_time}–{f.end_time}</span>}
          </div>
          {loadingAvail ? <div className="text-xs text-slate-400 mt-2">Checking availability…</div> : avail?.closed ? (
            <div className="text-xs text-amber-600 mt-2" data-testid="book-closed-note">Office is closed on this date. {isEmergency ? "Enter a custom time below (emergency bypasses working hours)." : "Choose emergency to override, or pick another date."}</div>
          ) : (
            <div className="mt-2" data-testid="book-slots">
              {slots.length === 0 ? <div className="text-xs text-slate-400">No slots configured for this day.</div> : (
                <SlotGrid slots={f.staff_id ? slots : slots.map((s) => ({ ...s, available: true }))} value={f.start_time} onPick={pickSlot} duration={avail?.duration || 30} testid="book-slot" />
              )}
            </div>
          )}
          {(isEmergency || avail?.closed) && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><label className="text-xs font-medium text-slate-500">Custom start</label><input data-testid="book-custom-start" type="time" value={f.start_time} onChange={(e) => setStart(e.target.value)} className={inputCls} /></div>
              <div><label className="text-xs font-medium text-slate-500">End</label><input data-testid="book-custom-end" type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className={inputCls} /></div>
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="book-emergency" checked={f.is_emergency} onChange={(e) => setF({ ...f, is_emergency: e.target.checked })} /> <Zap className="h-4 w-4 text-primary" /> Emergency appointment {settings?.emergency?.price ? `(+$${settings.emergency.price})` : ""}</label>
        {isEmergency && <p className="text-[11px] text-amber-600 -mt-3" data-testid="book-emergency-rules">Emergency rules: within the next 48 hours, between 08:00–21:00 (weekends/holidays OK), a reason is required, and overlapping a consultant is allowed.</p>}

        <div><label className="text-xs font-medium text-slate-500">Notes {isEmergency && <span className="text-primary">— reason required for emergencies *</span>}</label><textarea data-testid="book-notes" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inputCls} /></div>

        <button data-testid="book-submit" onClick={submit} disabled={saving} className="w-full bg-primary text-white px-5 py-3 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"><CalendarCheck className="h-4 w-4" /> {saving ? "Booking…" : "Confirm booking"}</button>
      </Card>
    </div>
  );
}
